import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getEnums } from "../../Redux/Actions/enumsActions";
import "./agentMarginDetailsPopup.css";

const AgentMarginDetailsPopup = ({ visible, onClose, existingData, onSave }) => {
    const isEdit = existingData && Object.keys(existingData).length > 0;
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const dispatch = useDispatch();
    const enums = useSelector((state) => state.enumsData?.enumsData?.data);
    const doorittPartnerRoles = enums?.doorittPartnerRoles || {};

    useEffect(() => {
        if (!enums) dispatch(getEnums());
    }, []);

    const [formData, setFormData] = useState({
        role: "",
        note: "",
        agent_margin: "",
        admin_margin: "",
        other_margin: "",
        lead_from: "self",
    });

    useEffect(() => {
        if (visible && existingData && Object.keys(existingData).length > 0) {
            setFormData({
                role: existingData.role || "",
                note: existingData.note || "",
                agent_margin: existingData.agent_margin ?? "",
                admin_margin: existingData.admin_margin ?? "",
                other_margin: existingData.other_margin ?? "",
                lead_from: existingData.lead_from || "self",
            });
        } else if (visible) {
            setFormData({ role: "", note: "", agent_margin: "", admin_margin: "", other_margin: "", lead_from: "self" });
            setErrors({});
        }
    }, [visible, existingData, doorittPartnerRoles]);

    if (!visible) return null;

    const clearError = (field) => {
        setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        clearError(name);
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.role.trim()) newErrors.role = "Role is required";
        if (formData.agent_margin === "" || formData.agent_margin === null) newErrors.agent_margin = "Agent margin is required";
        if (formData.admin_margin === "" || formData.admin_margin === null) newErrors.admin_margin = "Admin margin is required";

        // total must not exceed 100
        const total =
            Number(formData.agent_margin || 0) +
            Number(formData.admin_margin || 0) +
            Number(formData.other_margin || 0);

        if (total > 100) {
            newErrors.agent_margin = `Total margin exceeds 100% (current: ${total}%)`;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        try {
            setLoading(true);
            const payload = {
                role: formData.role,
                note: formData.note,
                agent_margin: Number(formData.agent_margin),
                admin_margin: Number(formData.admin_margin),
                other_margin: formData.other_margin ? Number(formData.other_margin) : 0,
                lead_from: formData.lead_from,
            };
            await onSave(payload);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="amdp-overlay">
            <div className="amdp-modal">

                <div className="amdp-modal__head">
                    <div className="amdp-modal__head-left">
                        <div className="amdp-modal__icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="1" x2="12" y2="23" />
                                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                            </svg>
                        </div>
                        <div>
                            <p className="amdp-modal__title">{isEdit ? "Edit Margin Details" : "Add Margin Details"}</p>
                            <p className="amdp-modal__subtitle">Fill in the agent margin information</p>
                        </div>
                    </div>
                    <button className="amdp-modal__close" onClick={onClose} disabled={loading}>✕</button>
                </div>

                <div className="amdp-modal__body">

                    <div className="amdp-section">
                        <div className="amdp-section__title">Basic Info</div>
                        <div className="amdp-grid-2">
                            <div className="amdp-field amdp-field--full">
                                <label className="amdp-label">Role <span className="amdp-req">*</span></label>
                                <select
                                    className={`amdp-input ${errors.role ? "amdp-input--error" : ""}`}
                                    name="role"
                                    value={formData.role}
                                    onChange={handleChange}
                                    disabled={loading}
                                >
                                    <option value="">Select role</option>
                                    {Object.entries(doorittPartnerRoles).map(([key, label]) => (
                                        <option key={key} value={label}>  
                                            {label.charAt(0).toUpperCase() + label.slice(1)}
                                        </option>
                                    ))}
                                </select>
                                {errors.role && <span className="amdp-error-msg">{errors.role}</span>}
                            </div>

                            <div className="amdp-field amdp-field--full">
                                <label className="amdp-label">Lead From <span className="amdp-req">*</span></label>
                                <div style={{ display: "flex", gap: "16px", marginTop: "4px" }}>
                                    {["self", "by_dooritt"].map((opt) => (
                                        <label key={opt} style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "14px" }}>
                                            <input
                                                type="radio"
                                                name="lead_from"
                                                value={opt}
                                                checked={formData.lead_from === opt}
                                                onChange={handleChange}
                                                disabled={loading}
                                            />
                                            {opt === "self" ? "Self" : "By Dooritt"}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="amdp-field amdp-field--full">
                                <label className="amdp-label">Note <span className="amdp-optional">(Optional)</span></label>
                                <textarea
                                    className="amdp-textarea"
                                    name="note"
                                    value={formData.note}
                                    onChange={handleChange}
                                    placeholder="Add a note..."
                                    disabled={loading}
                                    rows={2}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="amdp-divider" />

                    <div className="amdp-section">
                        <div className="amdp-section__title">Margin Details</div>
                        <div className="amdp-grid-2">
                            <div className="amdp-field">
                                <label className="amdp-label amdp-label--agent">Agent Margin <span className="amdp-req">*</span></label>
                                <div className="amdp-input-wrap">
                                    <input
                                        className={`amdp-input amdp-input--suffixed ${errors.agent_margin ? "amdp-input--error" : ""}`}
                                        type="number"
                                        name="agent_margin"
                                        value={formData.agent_margin}
                                        onChange={handleChange}
                                        onWheel={(e) => e.target.blur()}
                                        placeholder="0"
                                        disabled={loading}
                                        min={0}
                                    />
                                    <span className="amdp-input-suffix">%</span>
                                </div>
                                {errors.agent_margin && <span className="amdp-error-msg">{errors.agent_margin}</span>}
                            </div>

                            <div className="amdp-field">
                                <label className="amdp-label amdp-label--admin">Admin Margin <span className="amdp-req">*</span></label>
                                <div className="amdp-input-wrap">
                                    <input
                                        className={`amdp-input amdp-input--suffixed ${errors.admin_margin ? "amdp-input--error" : ""}`}
                                        type="number"
                                        name="admin_margin"
                                        value={formData.admin_margin}
                                        onChange={handleChange}
                                        onWheel={(e) => e.target.blur()}
                                        placeholder="0"
                                        disabled={loading}
                                        min={0}
                                    />
                                    <span className="amdp-input-suffix">%</span>
                                </div>
                                {errors.admin_margin && <span className="amdp-error-msg">{errors.admin_margin}</span>}
                            </div>

                            <div className="amdp-field">
                                <label className="amdp-label">Other Margin <span className="amdp-optional">(Optional)</span></label>
                                <div className="amdp-input-wrap">
                                    <input
                                        className="amdp-input amdp-input--suffixed"
                                        type="number"
                                        name="other_margin"
                                        value={formData.other_margin}
                                        onChange={handleChange}
                                        onWheel={(e) => e.target.blur()}
                                        placeholder="0"
                                        disabled={loading}
                                        min={0}
                                    />
                                    <span className="amdp-input-suffix">%</span>
                                </div>
                            </div>

                            {/* <div className="amdp-field amdp-field--full">
                                <p style={{
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    color: (
                                        Number(formData.agent_margin || 0) +
                                        Number(formData.admin_margin || 0) +
                                        Number(formData.other_margin || 0)
                                    ) > 100 ? "#dc2626" : "#16a34a",
                                    margin: 0,
                                }}>
                                    Total: {
                                        Number(formData.agent_margin || 0) +
                                        Number(formData.admin_margin || 0) +
                                        Number(formData.other_margin || 0)
                                    }% / 100%
                                </p>
                            </div> */}
                        </div>
                    </div>

                </div>

                <div className="amdp-modal__foot">
                    <button className="amdp-btn amdp-btn--secondary" onClick={onClose} disabled={loading}>
                        Cancel
                    </button>
                    <button className="amdp-btn amdp-btn--primary" onClick={handleSubmit} disabled={loading}>
                        {loading ? "Saving..." : isEdit ? "Update" : "Save"}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default AgentMarginDetailsPopup;